/**
 * Cache Invalidation Utilities
 * Handles automatic cache clearing when app version changes
 */

import { clearAllAnimationCaches } from './animationCache';

// Version constants
const CACHE_VERSION_KEY = 'app-cache-version';
const LAST_DEPLOYMENT_KEY = 'last-deployment-timestamp';

/**
 * Get current app version from environment or meta tag
 * @returns {string} Current app version
 */
export const getCurrentAppVersion = () => {
  // Try environment variable first
  if (process.env.REACT_APP_VERSION) {
    return process.env.REACT_APP_VERSION;
  }

  // Fall back to meta tag
  const versionMeta = document.querySelector('meta[name="app-version"]');
  if (versionMeta) {
    return versionMeta.getAttribute('content') || 'unknown';
  }

  // Default fallback
  return 'unknown';
};

/**
 * Get current deployment timestamp from environment or meta tag
 * @returns {string} Current deployment timestamp
 */
export const getCurrentDeploymentTimestamp = () => {
  // Try environment variable first
  if (process.env.REACT_APP_DEPLOYMENT_TIMESTAMP) {
    return process.env.REACT_APP_DEPLOYMENT_TIMESTAMP;
  }

  // Fall back to meta tag
  const timestampMeta = document.querySelector('meta[name="deployment-timestamp"]');
  if (timestampMeta) {
    return timestampMeta.getAttribute('content') || Date.now().toString();
  }

  // Default fallback
  return Date.now().toString();
};

/**
 * Get stored app version from localStorage
 * @returns {string|null} Stored app version
 */
export const getStoredAppVersion = () => {
  try {
    return localStorage.getItem(CACHE_VERSION_KEY);
  } catch (error) {
    console.error('[CacheInvalidation] Error reading stored version:', error);
    return null;
  }
};

/**
 * Get stored deployment timestamp from localStorage
 * @returns {string|null} Stored deployment timestamp
 */
export const getStoredDeploymentTimestamp = () => {
  try {
    return localStorage.getItem(LAST_DEPLOYMENT_KEY);
  } catch (error) {
    console.error('[CacheInvalidation] Error reading stored timestamp:', error);
    return null;
  }
};

/**
 * Store current app version in localStorage
 * @param {string} version - Version to store
 */
export const storeAppVersion = (version) => {
  try {
    localStorage.setItem(CACHE_VERSION_KEY, version);
    console.log(`[CacheInvalidation] Stored app version: ${version}`);
  } catch (error) {
    console.error('[CacheInvalidation] Error storing version:', error);
  }
};

/**
 * Store current deployment timestamp in localStorage
 * @param {string} timestamp - Timestamp to store
 */
export const storeDeploymentTimestamp = (timestamp) => {
  try {
    localStorage.setItem(LAST_DEPLOYMENT_KEY, timestamp);
    console.log(`[CacheInvalidation] Stored deployment timestamp: ${timestamp}`);
  } catch (error) {
    console.error('[CacheInvalidation] Error storing timestamp:', error);
  }
};

/**
 * Clear all application caches
 * Includes localStorage, animation caches, and other stored data
 */
export const clearAllAppCaches = () => {
  console.log('[CacheInvalidation] Clearing all application caches...');

  try {
    // Clear animation caches
    clearAllAnimationCaches();

    // Clear Redux state related items (keep essential data)
    const keysToKeep = [
      'sessionToken',
      'userProfile',
      'selectedStore',
      CACHE_VERSION_KEY,
      LAST_DEPLOYMENT_KEY
    ];

    const allKeys = Object.keys(localStorage);
    const keysToRemove = allKeys.filter(key => !keysToKeep.includes(key));

    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
        console.log(`[CacheInvalidation] Cleared cache key: ${key}`);
      } catch (error) {
        console.error(`[CacheInvalidation] Error clearing key ${key}:`, error);
      }
    });

    console.log(`[CacheInvalidation] Cleared ${keysToRemove.length} cache entries`);
  } catch (error) {
    console.error('[CacheInvalidation] Error during cache clearing:', error);
  }
};

/**
 * Check if app version has changed and clear caches if needed
 * @returns {boolean} True if version changed and caches were cleared
 */
export const checkAndClearCacheIfNeeded = () => {
  const currentVersion = getCurrentAppVersion();
  const currentTimestamp = getCurrentDeploymentTimestamp();
  const storedVersion = getStoredAppVersion();
  const storedTimestamp = getStoredDeploymentTimestamp();

  console.log('[CacheInvalidation] Version check:', {
    currentVersion,
    storedVersion,
    currentTimestamp,
    storedTimestamp
  });

  // Check if version or deployment timestamp changed
  const versionChanged = storedVersion && storedVersion !== currentVersion;
  const timestampChanged = storedTimestamp && storedTimestamp !== currentTimestamp;

  if (versionChanged || timestampChanged) {
    console.log('[CacheInvalidation] Version or deployment changed, clearing caches...');

    // Clear all caches
    clearAllAppCaches();

    // Update stored version and timestamp
    storeAppVersion(currentVersion);
    storeDeploymentTimestamp(currentTimestamp);

    return true;
  } else if (!storedVersion || !storedTimestamp) {
    // First time or missing data - store current values
    console.log('[CacheInvalidation] First run or missing stored data, storing current version');
    storeAppVersion(currentVersion);
    storeDeploymentTimestamp(currentTimestamp);
  }

  return false;
};

/**
 * Force cache refresh (for manual use)
 * Clears all caches and reloads the page
 */
export const forceCacheRefresh = () => {
  console.log('[CacheInvalidation] Force cache refresh initiated');

  // Clear all caches
  clearAllAppCaches();

  // Update stored values to current
  const currentVersion = getCurrentAppVersion();
  const currentTimestamp = getCurrentDeploymentTimestamp();
  storeAppVersion(currentVersion);
  storeDeploymentTimestamp(currentTimestamp);

  // Reload page to get fresh content
  window.location.reload(true);
};

/**
 * Get cache status information
 * @returns {Object} Cache status details
 */
export const getCacheStatus = () => {
  return {
    currentVersion: getCurrentAppVersion(),
    storedVersion: getStoredAppVersion(),
    currentTimestamp: getCurrentDeploymentTimestamp(),
    storedTimestamp: getStoredDeploymentTimestamp(),
    cacheSize: (() => {
      try {
        const totalSize = Object.keys(localStorage).reduce((total, key) => {
          const value = localStorage.getItem(key);
          return total + (value ? new Blob([value]).size : 0);
        }, 0);
        return totalSize;
      } catch (error) {
        return 0;
      }
    })()
  };
};

// Make forceCacheRefresh available globally for debugging
if (typeof window !== 'undefined') {
  window.forceCacheRefresh = forceCacheRefresh;
  window.getCacheStatus = getCacheStatus;
  window.clearAllAppCaches = clearAllAppCaches;
}